/**
 * IdeaDetailDrawer
 *
 * Creator chrome for the idea detail UI. Maps the slots returned from
 * ``useIdeaDetailContent`` to the shared ``Drawer`` primitive. All
 * chrome (close button, padding, responsive bottom-sheet ↔ right-panel
 * motion, focus trap, escape handling) lives in ``Drawer`` so this file
 * stays a thin idea-specific wrapper.
 *
 * Why a separate component from ``IdeaDetailModal``: admin uses the
 * centered modal, creator uses the edge-attached drawer. A previous
 * viewport-conditional Radix wrapper stacked two portals at the same
 * z-index and painted a blank white sheet on mobile (commit d056427),
 * so we keep the two surfaces context-keyed at the call site.
 */

import React from "react";
import { Drawer } from "@/components/ui/Drawer";
import { useIdeaDetailContent } from "./useIdeaDetailContent";

interface IdeaDetailDrawerProps {
  ideaId: string | null;
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  hideInternalResources?: boolean;
  /** Extra chrome rendered above the drawer footer (creator upload). */
  footerExtra?: React.ReactNode;
}

export const IdeaDetailDrawer: React.FC<IdeaDetailDrawerProps> = (props) => {
  const { isOpen, onClose, footerExtra } = props;
  const { header, body, footer, sideEffects } = useIdeaDetailContent(props);

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={header}
        footer={
          footerExtra ? (
            <>
              {footerExtra}
              {footer}
            </>
          ) : (
            footer
          )
        }
        bodyPadding
        a11yTitle="Idea Detail"
        a11yDescription="View and edit the selected idea."
      >
        {body}
      </Drawer>
      {sideEffects}
    </>
  );
};
